import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Lock } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { TracksManager } from "@/components/admin/TracksManager";
import { ArtistsManager } from "@/components/admin/ArtistsManager";
import { PlaylistsManager } from "@/components/admin/PlaylistsManager";
import { Submissions } from "@/components/admin/Submissions";

export const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

const detailToString = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong";
};

const tabs = [
  { id: "tracks", label: "Tracks" },
  { id: "artists", label: "Artists" },
  { id: "playlists", label: "Playlists" },
  { id: "submissions", label: "Submissions" },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("tracks");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {}).finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      toast.success("Access granted");
    } catch (err) {
      toast.error(detailToString(err.response?.data?.detail) || "Login failed");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center font-mono-tech text-xs tracking-[0.3em] text-slate-500 uppercase">Loading...</div>;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm bg-[#0c0c12] border border-white/10 p-8 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <LogoMark className="h-10 w-10" />
            <div>
              <h1 className="font-display text-lg font-bold text-white uppercase">Label Access</h1>
              <p className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500 uppercase">Admin only</p>
            </div>
          </div>
          <input data-testid="admin-login-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#08080c] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-[#00f0ff]" />
          <input data-testid="admin-login-password" required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-[#08080c] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-[#00f0ff]" />
          <button data-testid="admin-login-submit" type="submit" className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#00f0ff] text-black font-mono-tech text-xs tracking-[0.25em] uppercase font-bold hover:bg-white transition-colors">
            <Lock size={13} /> Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 glass-dark sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <span className="font-display text-sm font-bold text-white uppercase">Label Console</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono-tech text-[10px] tracking-[0.2em] text-slate-500 uppercase">{user.email}</span>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-slate-400 hover:text-[#ff0055] transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-px bg-white/10 border border-white/10 w-fit mb-10">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-6 py-3 font-mono-tech text-[11px] tracking-[0.2em] uppercase transition-colors ${tab === t.id ? "bg-[#00f0ff] text-black font-bold" : "bg-[#0c0c12] text-slate-400 hover:text-white"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "tracks" && <TracksManager />}
        {tab === "artists" && <ArtistsManager />}
        {tab === "playlists" && <PlaylistsManager />}
        {tab === "submissions" && <Submissions />}
      </main>
    </div>
  );
}
